
/**
 * Presentation metadata for the subscription screens.
 *
 * Shared by the web and mobile views so the two can never disagree about what a
 * status means — the copy, the tone and the call to action live here once.
 */

import { isIranMarket } from '@parsina/framework/ui/lib/brand';
import { formatAmount } from '@parsina/framework/ui/lib/currency';

import { isUnknownCurrency, unitLabel } from '../../lib/currency';

// ── Platform money (PM-0211, docs/platform-money/CONTRACT.md) ───────────────
//
// What a clinic pays US is in the DEPLOYMENT's currency — IRR for Parsina, USD
// for Proxia — never the clinic's own (a Swiss clinic keeps CHF books and pays
// its subscription in dollars). So none of this goes through the clinic-bound
// `formatMoney`/`moneyUnit`: a plan says its own `currency`, and blank is IRR.
//
// IRR keeps EVERY figure, sentence and computation exactly as before: toman
// from the server's `priceToman`, the catalogue's toman sentences. Any other
// currency reads the minor-unit fields and is formatted by the framework's
// `formatAmount` (2 decimals for USD) beside the currency's own unit.

/** A plan's (or a period's) platform currency. Blank / absent = IRR. */
export const planCurrency = (row) => row?.currency || 'IRR';

const isIrr = (row) => planCurrency(row) === 'IRR';

/**
 * A plan's price in the units this screen computes with: TOMAN for IRR —
 * exactly the figure it always used — and MINOR UNITS for any other currency.
 * Savings, percentages and break-even years are ratios or differences within
 * one plan list, so they hold in either.
 */
export const planPrice = (plan) => Number((isIrr(plan) ? plan?.priceToman : plan?.priceMinor) || 0);
export const planCompareAt = (plan) => Number((isIrr(plan) ? plan?.compareAtPriceToman : plan?.compareAtPriceMinor) || 0);

/**
 * Minor units of a non-IRR platform currency → the figure a human reads
 * (`unit`: 'none' | 'short'). A code this build does not know is shown as its
 * raw minor units and its code, never under «تومان» (lib/currency.js).
 */
export function formatPlatformAmount(minor, currency, { locale = 'fa', unit = 'none' } = {}) {
  if (isUnknownCurrency(currency)) {
    const figure = String(Number(minor));
    return unit === 'none' ? figure : `${figure} ${unitLabel(currency, locale, unit)}`;
  }
  return formatAmount(minor, currency, { locale, unit });
}

/** The unit word beside a non-IRR platform amount («USD»). */
export const platformUnit = (currency, locale) => unitLabel(currency, locale, 'short');

/**
 * How this deployment sells a plan — `myClinicCapabilities.billing.checkout`
 * (SUBSCRIPTION_CHECKOUT on the BFF). CONTACT: no payment rail, so no checkout,
 * no claim that the panel is read-only, and "Contact us" instead. Anything else
 * — including an older BFF that does not say — is ON, today.
 */
export const isContactCheckout = (capabilities) => capabilities?.billing?.checkout === 'CONTACT';

/**
 * Status → how it reads to a clinic.
 *
 * `tone` maps to the framework's semantic colors, not raw hex. `urgent` decides
 * whether the global banner shows at all: a healthy ACTIVE clinic must never see
 * a billing banner, and a TRIALING one sees an informational nudge, not an alarm.
 */
export const STATUS_META = (t) => ({
  TRIALING: {
    label: t('subscription.status.trial'),
    tone: 'info',
    urgent: false,
    description: t('subscription.status.trialHint')
  },
  ACTIVE: {
    label: t('common.state.active'),
    tone: 'success',
    urgent: false,
    description: t('subscription.status.activeHint')
  },
  PAST_DUE: {
    label: t('subscription.status.grace'),
    tone: 'warning',
    urgent: true,
    description: t('subscription.status.graceHint')
  },
  EXPIRED: {
    label: t('subscription.status.expired'),
    tone: 'danger',
    urgent: true,
    description: t('subscription.status.expiredHint')
  },
  SUSPENDED: {
    label: t('people.status.suspended'),
    tone: 'danger',
    urgent: true,
    description: t('subscription.status.suspendedHint')
  }
});

/**
 * `checkout` CONTACT swaps the two descriptions that promise a read-only panel
 * (a trial's and an expiry's) for ones that send the clinic to us instead — a
 * deployment with no way to pay never makes that claim.
 */
export const statusMeta = (status, t, checkout) => {
  const meta = STATUS_META(t)[status] || STATUS_META(t).ACTIVE;
  if (checkout !== 'CONTACT') return meta;
  if (status === 'TRIALING') return { ...meta, description: t('subscription.contact.trialHint') };
  if (status === 'EXPIRED') return { ...meta, description: t('subscription.contact.expiredHint') };
  return meta;
};

/**
 * The statuses that warrant a banner. The single source is `STATUS_META`'s own
 * `urgent` flags — derived rather than restated, so the two cannot drift — but
 * read through a translator that returns the key, because urgency is structure
 * and has nothing to say in any language.
 */
export const URGENT_STATUSES = new Set(
  Object.entries(STATUS_META((key) => key))
    .filter(([, meta]) => meta.urgent)
    .map(([status]) => status),
);

/** Framework `Badge`/`Alert` variant for a status tone. */
export const TONE_VARIANT = {
  info: 'outline',
  success: 'success',
  warning: 'warning',
  danger: 'destructive'
};

/**
 * What a clinic with no running plan keeps, stated positively.
 *
 * Since 2026-09-29 such a clinic — its 7-day trial ended unpaid, or its term
 * lapsed — is READ-ONLY: it sees everything and writes nothing. Leading with
 * what it keeps makes the message "you still own your data" rather than "you
 * are locked out". Cutting off a departing person's access is the one write it
 * keeps, and saying so matters to an owner deciding whether to pay.
 */
export const RETAINED_CAPABILITIES = (t) => ([
  t('subscription.retained.records'),
  t('subscription.retained.history'),
  t('subscription.retained.invoices'),
  t('subscription.retained.messages'),
  t('subscription.lost.staffAccess'),
]);

/** What stops — every write, named by the ones a desk notices first. */
export const BLOCKED_CAPABILITIES = (t) => ([
  t('subscription.lost.newBooking'),
  t('subscription.lost.reception'),
  t('subscription.lost.billing'),
  t('subscription.lost.messaging'),
  t('subscription.lost.anyEdit'),
]);

/**
 * ISO → «۴ مرداد ۱۴۰۵». Returns '' for a missing or unparseable date so callers
 * can omit the row rather than render «undefined».
 *
 * NOTE: `msToJalali` returns `{ y, m, d }` — not `{ jy, jm, jd }` — and returns
 * `null` for anything before 1400/01/01 rather than throwing.
 */
// `formatDate` threaded in: a plain helper, not a component. It used to build
// a Jalali date out of its own month list, so a subscription period read in
// Persian on an English panel.
export function formatJalaliDate(iso, formatDate) {
  // Empty string, not the formatters' em dash: an absent period end means "no
  // end", which the KPI strip renders as «نامحدود» — a dash there would read as
  // a subscription that ended on a date nobody recorded.
  if (!iso || Number.isNaN(new Date(iso).getTime())) return '';
  return formatDate(iso, 'long');
}

/**
 * Which date a clinic's status is read against — ONE answer for the KPI strip
 * and the status hero, which used to pick separately.
 *
 * `kind` is 'trial' | 'grace' | 'period' | 'unlimited' | 'none':
 * - a trial counts to `trialEndsAt` — and so does a trial that ENDED unpaid.
 *   That clinic has never had a paid period, so `currentPeriodEnd` is null, and
 *   reading it put «اعتبار تا: نامحدود» beside «منقضی شده»: an expired clinic
 *   told its access never ends;
 * - no date at all is «نامحدود» ONLY for a clinic that is still entitled — a
 *   grandfathered clinic or a دائمی purchase. A read-only clinic with no date
 *   is 'none' and renders a dash, never "unlimited".
 */
export function periodAnchor(capabilities, subscription) {
  if (!capabilities) {
    const iso = subscription?.currentPeriodEnd || null;
    if (iso) return { kind: 'period', iso };
    return { kind: ['EXPIRED', 'SUSPENDED'].includes(subscription?.status) ? 'none' : 'unlimited', iso: null };
  }
  if (capabilities.inTrial || capabilities.trialEnded) {
    return { kind: 'trial', iso: capabilities.trialEndsAt || null };
  }
  if (capabilities.inGracePeriod) return { kind: 'grace', iso: capabilities.graceEndsAt || null };
  if (capabilities.currentPeriodEnd) return { kind: 'period', iso: capabilities.currentPeriodEnd };
  return { kind: capabilities.readOnly || capabilities.entitled === false ? 'none' : 'unlimited', iso: null };
}

/**
 * Human remaining-time copy.
 *
 * Says «امروز» rather than «۰ روز» — a countdown that reads zero looks broken,
 * and «امروز» is the only phrasing that makes a 1-day grace actionable.
 */
// Takes `t` — a plain module function, not a component. Named `t` on purpose:
// docs/I18N.md explains why a forwarder keeps that name.
export function remainingLabel(days, t) {
  if (days == null) return '';
  if (days < 0) return t('subscription.remaining.ended');
  if (days === 0) return t('subscription.remaining.endsToday');
  // `count` is passed as a NUMBER so the plural rules select and the figure is
  // rendered by the locale's own number formatter. Persian resolves every
  // count to `_other`; English needs `_one` for the "1 day left" case that
  // used to be a separate hardcoded sentence here.
  return t('subscription.remaining.days', { count: days });
}

/**
 * Discount against a plan's own compare-at price, as a percentage.
 *
 * Reads `compareAtPriceToman`, which the server stores per plan, rather than
 * hunting the monthly plan and multiplying by twelve. That older approach
 * assumed ONE monthly and ONE annual plan in the whole catalog; with two paid
 * tiers it silently compared نقره‌ای's annual price against طلایی's monthly one
 * and produced a number that was not a discount on anything.
 *
 * Returns null when there is nothing to compare, so the UI omits the badge
 * rather than rendering a misleading «۰٪».
 */
export function savingPercent(plan) {
  const price = planPrice(plan);
  const compareAt = planCompareAt(plan);
  if (!price || !compareAt || compareAt <= price) return null;
  return Math.round(((compareAt - price) / compareAt) * 100);
}

/**
 * Tier presentation — label and the promise it makes.
 *
 * `capabilitySet` is the join key, matching what the BFF reports and what each
 * plan carries, so a card and a paywall message can never disagree about which
 * tier they are describing.
 *
 * Only طلایی is sold (2026-09-29: پایه and نقره‌ای withdrawn), so it lists the
 * whole panel rather than what it adds over a tier below. A clinic still mid-term
 * on نقره‌ای reads its tier's name from the server (`tierNameFa`); it needs no
 * card, because it can no longer be bought.
 *
 * دائمی is NOT here: it is a billing term over طلایی, carries `CLINIC_GOLD`, and
 * appears as a one-time purchase inside the طلایی card.
 */
export const TIER_META = (t) => ([
  {
    key: 'CLINIC_GOLD',
    nameFa: t('subscription.tiers.gold.name'),
    pitch: t('subscription.tiers.gold.pitch'),
    features: [
      t('subscription.features.scheduling'),
      t('subscription.features.patientBook'),
      t('subscription.features.cashier'),
      t('subscription.features.payments'),
      t('subscription.features.services'),
      t('subscription.features.contracts'),
      t('subscription.features.reports'),
      t('subscription.features.expenses'),
      t('subscription.features.inventory'),
      t('subscription.features.customRoles'),
      t('subscription.features.voice'),
    ],
    limits: { maxPatients: null, maxDoctors: null, maxStaff: null },
  },
]);

/**
 * The catalog, arranged the way it is sold: one card per tier, each holding the
 * ways to buy it.
 *
 * A plan is placed by its `capabilitySet`, never by its code — so adding a
 * six-month طلایی later is a row in the service's seed table and nothing here.
 * `oneTime` collects terms that never end (`periodMonths === 0`): دائمی.
 */
export function groupPlansByTier(plans, t) {
  return TIER_META(t).map((tier) => {
    const own = (plans || []).filter((p) => p.capabilitySet === tier.key);
    return {
      ...tier,
      monthly: own.find((p) => p.periodMonths === 1) || null,
      annual: own.find((p) => p.periodMonths === 12) || null,
      oneTime: own.filter((p) => p.periodMonths === 0 && planPrice(p) > 0),
    };
  });
}

/**
 * Promotions the panel can NAME, keyed by the code the subscription service
 * sends on each plan it repriced.
 *
 * Only a code crosses the wire, never Persian copy, so an English or Arabic
 * clinic reads the offer in its own language. A code the panel has never heard
 * of still renders — struck price, saving, a generic «تخفیف ویژه» — because the
 * service only sends a code when the stored price really is discounted; what is
 * missing then is a name, not a fact.
 */
export const PROMOTIONS = {
  CONGRESS_RESPIRATORY_1405: {
    label: 'subscription.promotions.congressRespiratory1405.label',
    title: 'subscription.promotions.congressRespiratory1405.title',
    lead: 'subscription.promotions.congressRespiratory1405.lead',
  },
};

const GENERIC_PROMOTION = {
  label: 'subscription.promotions.generic.label',
  title: null,
  lead: null,
};

/**
 * Catalogue keys for a promotion code, or null when no promotion is running —
 * always null outside the Iranian market, where no promotion is named.
 */
export function promotionCopy(code) {
  if (!code || !isIranMarket()) return null;
  return PROMOTIONS[code] || GENERIC_PROMOTION;
}

/**
 * How much a plan saves against its own compare-at price, in toman — the number
 * the offer card prints in bold. null when there is nothing to compare, so the
 * card omits the box rather than printing «۰ تومان سود».
 *
 * During a promotion compare-at is the REGULAR price, so this is the saving the
 * promotion gives; outside one it is the annual plan's saving over twelve
 * monthly payments. The card labels the two differently.
 */
export function savingToman(plan) {
  const price = planPrice(plan);
  const compareAt = planCompareAt(plan);
  if (!price || !compareAt || compareAt <= price) return null;
  return compareAt - price;
}

/**
 * The year from which a one-time دائمی purchase has cost less than paying
 * annually — «از سال سوم به بعد، عملاً رایگان». null outside 2–6, where the
 * line would either be trivially true or too far off to persuade anyone.
 *
 * floor(perpetual / annual) + 1, not ceil: at that year the annual payer has
 * paid MORE than the perpetual price, not merely as much. It compares against
 * the annual price on the card — the promotional one during a promotion — which
 * is the conservative reading: renewals at the regular price only bring the
 * year forward.
 */
export function breakEvenYear(perpetual, annual) {
  const once = planPrice(perpetual);
  const yearly = planPrice(annual);
  if (!once || !yearly) return null;
  const year = Math.floor(once / yearly) + 1;
  return year >= 2 && year <= 6 ? year : null;
}

/**
 * Whether the paywall banner should show for these capabilities.
 *
 * Reads `URGENT_STATUSES` rather than `statusMeta(...).urgent`, so a predicate
 * about money does not need a translator threaded into it just to reach a
 * boolean that sits beside some copy.
 */
export function shouldWarn(capabilities) {
  if (!capabilities || capabilities.unavailable) return false;
  // No way to pay: every banner but a suspension's would promise a read-only
  // panel or ask for a payment nobody can make.
  if (isContactCheckout(capabilities)) return capabilities.status === 'SUSPENDED';
  if (URGENT_STATUSES.has(capabilities.status)) return true;
  // Nudge during a trial that is nearly over, so the clinic is not surprised.
  return !!capabilities.inTrial && capabilities.daysRemaining != null && capabilities.daysRemaining <= 3;
}

